import { Checkbox } from '@/components/ui/checkbox';
import { cn } from '@/lib/utils';
import { CalendarDays, Users } from 'lucide-react';
import { OwnerAvatar } from '@/shared/components/OwnerAvatar';
import { TodoPriorityBadge } from './TodoPriorityBadge';
import type { EosTodo } from '../types';

interface Props {
  todo: EosTodo;
  onSelect: (todo: EosTodo) => void;
  onToggleComplete: (todo: EosTodo) => void;
  ownerLabel: (id: string) => string;
  teamLabel: (id: string | null) => string;
}

function dueLabel(iso: string): { text: string; overdue: boolean } {
  const today = new Date();
  today.setUTCHours(0, 0, 0, 0);
  const d = new Date(iso);
  d.setUTCHours(0, 0, 0, 0);
  const diff = Math.round((d.getTime() - today.getTime()) / 86400000);
  if (diff < 0) return { text: `${-diff}d overdue`, overdue: true };
  if (diff === 0) return { text: 'Today', overdue: false };
  if (diff === 1) return { text: 'Tomorrow', overdue: false };
  return { text: d.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' }), overdue: false };
}

export function TodoCard({ todo, onSelect, onToggleComplete, ownerLabel, teamLabel }: Props) {
  const due = dueLabel(todo.dueDate);
  const isDone = todo.status === 'done';
  const owner = ownerLabel(todo.ownerId);
  const team = todo.teamId ? teamLabel(todo.teamId) : null;
  return (
    <div
      className="rounded-md border bg-card p-3 shadow-sm hover:border-primary/40 transition-colors cursor-pointer"
      onClick={() => onSelect(todo)}
    >
      <div className="flex items-start gap-2">
        <Checkbox
          checked={isDone}
          onClick={(e) => { e.stopPropagation(); onToggleComplete(todo); }}
          className="mt-0.5"
          aria-label={isDone ? 'Reopen' : 'Complete'}
        />
        <div className="flex-1 min-w-0 space-y-1.5">
          <div className={cn('text-sm font-medium leading-snug break-words', isDone && 'line-through text-muted-foreground')}>
            {todo.title}
          </div>

          {todo.description && (
            <p className={cn('text-xs text-muted-foreground line-clamp-2 break-words', isDone && 'line-through')}>
              {todo.description}
            </p>
          )}

          <div className="flex items-center gap-2 flex-wrap">
            <TodoPriorityBadge priority={todo.priority} />
            <span className={cn('text-xs flex items-center gap-1', due.overdue ? 'text-destructive font-medium' : 'text-muted-foreground')}>
              <CalendarDays className="h-3 w-3" /> {due.text}
            </span>
          </div>

          <div className="flex items-center gap-3 text-xs text-muted-foreground">
            <span className="flex items-center gap-1.5 min-w-0">
              <OwnerAvatar
                ownerId={todo.ownerId}
                name={owner}
                className="h-5 w-5"
                fallbackClassName="text-[9px]"
              />
              <span className="truncate">{owner}</span>
            </span>
            {team && (
              <span className="flex items-center gap-1 min-w-0">
                <Users className="h-3 w-3 shrink-0" />
                <span className="truncate">{team}</span>
              </span>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
